import { useMutation, UseMutationResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { getAlerts, updateAlert } from "../../../api/homeSiteAlerts/homeSiteAlerts";
import { Alert, Alerts } from "../../../api/homeSiteAlerts/types";
import { ALERT_AUTORESOLVED_STATUS, ALERT_RESOLVED_STATUS } from "../../../utils/constants";

export const useAlertsData = (divcode: string) => {
  return useQuery<Alerts>({
    queryKey: ["homeSiteAlerts", divcode],
    queryFn: () => getAlerts({ filter: `divisionCode=${divcode},status=active,docStatus=true` }),
    enabled: !!divcode,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
    refetchIntervalInBackground: true,
  });
};

export const useAlertsDataByHomesiteId = (divcode: string, homesiteId: string) => {
  return useQuery<Alerts>({
    queryKey: ["alertsByHomesiteId", divcode, homesiteId],
    queryFn: () =>
      getAlerts({
        filter: `divisionCode=${divcode},homeSiteId=${homesiteId},docStatus=true`,
        order: "DESC",
        orderBy: "createdOnDate",
      }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
    refetchIntervalInBackground: true,
    enabled: !!divcode,
  });
};

export const useUpdateAlerts = (divCode: string,homesiteId: string): UseMutationResult<Alert, unknown, Partial<Alert>, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAlert,
    onSuccess: (updatedAlert: Alert) => {
      const updateAlertsData = (currentAlerts: Alerts | undefined) => {
        if (!currentAlerts) return { data: [] };
        return {
          ...currentAlerts,
          data: currentAlerts.data.map((alert: Alert) => (alert.id === updatedAlert.id ? updatedAlert : alert)),
        };
      };

      const updateHomeSiteAlerts = (currentAlerts: Alerts | undefined) => {
        if (!currentAlerts) return { data: [] };
        return {
          ...currentAlerts,
          data: currentAlerts.data
            .map((alert: Alert) => (alert.id === updatedAlert.id ? updatedAlert : alert))
            .filter((alert: Alert) => alert.status !== ALERT_RESOLVED_STATUS && alert.status !== ALERT_AUTORESOLVED_STATUS)
        };
      };

      // Apply different updates to different caches
      queryClient.setQueryData(["homeSiteAlerts", divCode], updateHomeSiteAlerts);
      queryClient.setQueryData(["alertsByHomesiteId", divCode, homesiteId], updateAlertsData);
    },
    onError: (error) => {
      console.error("Error updating alert:", error);
    },
  });
};
